// Pre-upload validation for a recorded clip.
//
// These checks used to live inline in ConversationWT's uploadAudio, where two of them
// bailed out with a bare `return`. That silently stranded the learner: the stop handler
// had already latched `loading` on, so the microphone stayed disabled with no error and
// no way forward but a page reload (MAD-788). Pulling them out makes every rejection
// name itself, so the caller can't forget to hand the microphone back.

export const MIN_RECORDING_BYTES = 100; // below this the clip is empty/invalid
export const MAX_RECORDING_BYTES = 25 * 1024 * 1024;

// Fallbacks for when i18next has no string for the key yet.
export const RECORDING_GUARD_FALLBACK_MESSAGES = {
  no_audio_detected: 'No audio detected. Please try speaking again.',
  audio_decode_error: 'Audio file could not be processed. Please try recording again.',
};

/**
 * Decides whether a recorded clip can be uploaded.
 *
 * @param {Blob|null|undefined} blob - The clip handed back by react-mic.
 * @returns {{ok: true} | {ok: false, reason: string, messageKey: string, details: object}}
 *   On rejection, `reason` is the telemetry label and `messageKey` the i18next key to show.
 */
export function classifyRecordedBlob(blob) {
  if (!blob || typeof blob.size !== 'number' || blob.size < MIN_RECORDING_BYTES) {
    return {
      ok: false,
      reason: 'empty_audio',
      messageKey: 'no_audio_detected',
      details: { blobSize: typeof blob?.size === 'number' ? blob.size : 0 },
    };
  }

  // A blob with an empty or non-audio type fails the server's decoder. Browsers do
  // occasionally hand back an untyped blob, and that must surface as a retry prompt
  // rather than a dead microphone.
  if (typeof blob.type !== 'string' || !blob.type.startsWith('audio/')) {
    return {
      ok: false,
      reason: 'unsupported_audio_type',
      messageKey: 'audio_decode_error',
      details: { blobType: blob.type || '(empty)' },
    };
  }

  if (blob.size > MAX_RECORDING_BYTES) {
    return {
      ok: false,
      reason: 'audio_too_large',
      messageKey: 'audio_decode_error',
      details: { blobSize: blob.size },
    };
  }

  return { ok: true };
}
